import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Trans, useLingui } from "@lingui/react/macro";
import { CalendarClock, RefreshCw } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { QueryErrorState } from "@/components/ui/query-error-state";
import { formatPercent } from "@/lib/format";
import { cn } from "@/lib/utils";
import { formatDate } from "@/utils/dateUtils";
import { BaseEditableTable } from "@/components/table/BaseEditableTable";
import type { EditableCellConfig } from "@/components/table/types";
import { useExpiringBeitragsfaktoren } from "@/hooks/useExpiringBeitragsfaktoren";
import type { billing_ExpiringBFContractDTO } from "@/api/models/billing_ExpiringBFContractDTO";
import { SubsidyRenewalDialog } from "./SubsidyRenewalDialog";
import { FormulaTip } from "@/components/ui/formula-tip";
import { FORMULA } from "@/lib/analytics/formulas";

type ExpiringRow = billing_ExpiringBFContractDTO & { id: string };

/** The widget's anchor on the Dashboard. */
export const SUBSIDY_EXPIRY_ANCHOR = "subsidy-expiry";

interface BfExpiringWidgetProps {
  daysAhead?: number;
  className?: string;
  /** False while the Dashboard summary that fills this widget's entry is loading. */
  enabled?: boolean;
}

/**
 * Expiring and expired subsidy documents (#567, #2004, #2007): one row per
 * contract version and document (Beitragsfaktor, SBU or KmbB review meeting), expired rows first with an
 * "Expired" badge. A row stays until the renewal is entered; the renew button
 * opens SubsidyRenewalDialog without leaving the dashboard.
 */
export function BfExpiringWidget({ daysAhead = 90, className, enabled = true }: BfExpiringWidgetProps) {
  const { t, i18n } = useLingui();
  const navigate = useNavigate();
  const [renewing, setRenewing] = useState<billing_ExpiringBFContractDTO | null>(null);
  // Pending, not loading: waiting for the summary reads as loading, not as an empty list.
  const { data, isPending: isLoading, error, refetch } = useExpiringBeitragsfaktoren(daysAhead, { enabled });
  // The Dashboard's attention row links here (#2175 C4).
  const location = useLocation();
  const cardRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (location.hash === `#${SUBSIDY_EXPIRY_ANCHOR}`) cardRef.current?.scrollIntoView?.({ block: "start" });
  }, [location.hash]);

  // A local const named `days` so the Trans macro emits the `{days}` placeholder.
  const days = daysAhead;

  const tableData: ExpiringRow[] = useMemo(
    () => (data?.items ?? []).map((r) => ({ ...r, id: `${r.contract_id ?? ""}-${r.document ?? ""}` })),
    [data],
  );

  const columns: EditableCellConfig<ExpiringRow>[] = useMemo(
    () => [
      {
        accessorKey: "child_last_name",
        header: t({ id: "dashboard.bfExpiring.col.child", message: "Child" }),
        editable: false,
        sortable: true,
        inputType: "text",
        cell: ({ row }) => {
          const r = row.original;
          const name = [r.child_first_name, r.child_last_name].filter(Boolean).join(" ");
          const internal = r.child_internal_id ? ` (${r.child_internal_id})` : "";
          return (
            <span className="font-medium">
              {name || r.child_id}
              {internal}
            </span>
          );
        },
      },
      {
        accessorKey: "contract_number",
        header: t({ id: "dashboard.bfExpiring.col.contract", message: "Contract" }),
        editable: false,
        sortable: false,
        inputType: "text",
        cell: ({ row }) => row.original.contract_number ?? "-",
      },
      {
        accessorKey: "document",
        header: t({ id: "dashboard.subsidyExpiry.col.document", message: "Document" }),
        editable: false,
        sortable: true,
        inputType: "text",
        cell: ({ row }) => {
          const r = row.original;
          if (r.document === "sbu") {
            return <span>{t({ id: "subsidyExpiry.document.sbu", message: "SBU" })}</span>;
          }
          if (r.document === "kmbb") {
            return <span>{t({ id: "subsidyExpiry.document.kmbb", message: "KmbB review meeting" })}</span>;
          }
          const bf = typeof r.beitragsfaktor === "number" ? ` ${formatPercent(r.beitragsfaktor, i18n.locale, 2)}` : "";
          return (
            <span>
              {t({ id: "dashboard.subsidyExpiry.document.bf", message: "BF" })}
              {bf}
            </span>
          );
        },
      },
      {
        accessorKey: "valid_until",
        header: t({ id: "dashboard.bfExpiring.col.validUntil", message: "Valid until" }),
        editable: false,
        sortable: true,
        inputType: "date",
        cell: ({ row }) => {
          const r = row.original;
          const docUntil = r.document_valid_until ? formatDate(r.document_valid_until) : "";
          return (
            <span className="flex flex-col">
              <span>{r.valid_until ? formatDate(r.valid_until) : "-"}</span>
              {docUntil ? (
                <span className="text-xs text-muted-foreground" data-testid="bf-expiring-document-until">
                  <Trans id="dashboard.subsidyExpiry.documentUntil">Confirmation: {docUntil}</Trans>
                </span>
              ) : null}
            </span>
          );
        },
      },
      {
        accessorKey: "days_remaining",
        header: t({ id: "dashboard.bfExpiring.col.daysRemaining", message: "Days remaining" }),
        editable: false,
        sortable: true,
        inputType: "number",
        cell: ({ row }) => {
          const d = row.original.days_remaining;
          if (typeof d !== "number") return "-";
          // Text-bearing badge, so "expired" is never colour-only.
          return d < 0 ? (
            <Badge variant="destructive" data-testid="bf-expiring-expired">
              {t({ id: "subsidyExpiry.badge.expired", message: "Expired" })}
            </Badge>
          ) : (
            `${d}`
          );
        },
      },
      {
        accessorKey: "contract_group_id",
        header: "",
        editable: false,
        sortable: false,
        inputType: "text",
        cell: ({ row }) => (
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid={`bf-expiring-renew-${row.original.id}`}
            onClick={(e) => {
              // The row itself opens the contract.
              e.stopPropagation();
              setRenewing(row.original);
            }}
          >
            <RefreshCw className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
            {t({ id: "dashboard.subsidyExpiry.renew", message: "Renew" })}
          </Button>
        ),
      },
    ],
    [t, i18n.locale],
  );

  const handleRowClick = (row: ExpiringRow) => {
    if (row.contract_id) navigate(`/contracts/${row.contract_id}/edit`);
  };

  return (
    <Card ref={cardRef} id={SUBSIDY_EXPIRY_ANCHOR} className={cn("min-w-0 scroll-mt-24", className)}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <CalendarClock className="h-4 w-4" />
              <Trans id="dashboard.subsidyExpiry.heading">Subsidy dates expiring</Trans>
              <FormulaTip content={i18n._(FORMULA.dashboardSubsidyDaysRemaining)} />
            </CardTitle>
            <CardDescription>
              <Trans id="dashboard.subsidyExpiry.summary">
                Beitragsfaktor, SBU and KmbB dates that expire in the next {days} days or have passed
              </Trans>
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
          </div>
        ) : error ? (
          <QueryErrorState
            compact
            error={error}
            onRetry={() => void refetch()}
            title={<Trans id="dashboard.bfExpiring.error">Failed to load expiring contracts.</Trans>}
          />
        ) : tableData.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            <Trans id="dashboard.subsidyExpiry.none">
              No Beitragsfaktor, SBU or KmbB date expires in the next {days} days.
            </Trans>
          </p>
        ) : (
          <BaseEditableTable<ExpiringRow>
            data={tableData}
            columns={columns}
            isLoading={false}
            onCellUpdate={async () => {
              /* read-only */
            }}
            onRowClick={handleRowClick}
          />
        )}
      </CardContent>
      <SubsidyRenewalDialog row={renewing} onOpenChange={(open) => !open && setRenewing(null)} />
    </Card>
  );
}
